import React, { useContext } from 'react';
import _ from 'lodash';
import getDeviceType from '../utils/getDeviceType';
import { CONFIG, MODELS } from '../common/steps';

export const createModelInfo = () => {
  const {
    appResources,
    UI_PATH: uiPath,
    CONTEXT_PATH: contextPath,
    AUTH_CONTEXT: auth,
    VERSION: version,
  } = window;
  const {
    language,
    country,
    brand,
    brandBase,
    brandVariant,
    assetsBrand,
    carline,
    year,
    bodyStyle,
    region,
    featureGroup,
    evMode,
  } = appResources;

  const legacyServicesPath = `${contextPath}/services`;
  const servicesPath = `${contextPath}/api/v2`;
  const v3ServicesPath = `${contextPath}/api/v3`;
  const modelPath = `${language}/${country}/${brand}/${carline}/${year}/${bodyStyle}`;

  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  const lccKey = params.LCC ? 'LCC' : 'lcc';
  const transactionIdKey = `${modelPath}/${lccKey}`;
  const lccValue = params[lccKey];
  const transactionId = lccValue;
  const hasTransactionId = !!lccValue;

  const steps = _.get(appResources, 'processSteps', []).map((step) => {
    const path = `/${step.key.toLowerCase()}`.replaceAll('.', '');
    return {
      ...step,
      path,
      matchPath: `${path}/*`,
    };
  });
  const urls = _.get(appResources, 'urls', {});
  const display = _.get(appResources, 'display', {});
  const isAccessoriesOpenDefault = _.get(display, 'accessoriesOpen', false);
  const isLiteRenderMode = appResources.renderMode === 'LITE';
  const isDefaultRenderMode = !isLiteRenderMode;
  const baseUrl = _.get(appResources, 'urls.baseUrl', '');
  const homepageUrl = _.get(appResources, 'urls.homepageUrl');
  const backOutLocationUrl = _.get(appResources, 'urls.backOutLocationUrl');
  const classicModel = _.get(display, 'classicModel', false);
  const deviceType = getDeviceType();
  const enableTrimFilterCollapseSingleDrive = _.get(
    display,
    'enableTrimFilterCollapseSingleDrive',
    false
  );
  const enableConfigStep = steps.length > 0 && steps[0].key === CONFIG;
  const enableModelsStep = steps.length > 0 && steps[0].key === MODELS;
  const enableFallbackNavigation = _.get(
    display,
    'enableFallbackNavigation',
    false
  );
  const enableTrimCompare = !_.get(display, 'disableTrimCompare', false);
  const enablePaymentEstimator = _.get(
    display,
    'showPaymentEstimatorComponent',
    true
  );
  const enableChat = _.get(display, 'useLivePersonChat', false);
  const liveChatType = _.get(display, 'liveChatType', 'salesforce');
  const enableAuth = _.get(auth, 'enabled', false);
  const enableFeaturedBrandPromo = _.get(
    display,
    'enableFeaturedBrandPromo',
    false
  );
  const enableFeaturedSubscription = _.get(
    display,
    'enableFeaturedSubscription',
    false
  );

  const title = _.get(appResources, 'bodyStyleTitle', '');

  const baseVehicleShopperParamsUrl = _.get(
    appResources,
    'urls.vehicleShopperParamsUrl',
    ''
  );

  const vehicleShopperParamsUrl = `${baseVehicleShopperParamsUrl}${brandBase}/${language}_${country}/`;
  const enableVehicleShopper = _.get(display, 'enableVehicleShopper', false);
  const assetsUrlTemplate = `${uiPath}/assets/brands/${brandBase}/images/\${filename}`;

  const isFamilyFirstCA = brandVariant === 'ff' && country === 'CA';

  const isFamilyFirstUS =
    (brandVariant === 'ff' && country === 'US') ||
    brandVariant === 'de' ||
    brandVariant === 'co' ||
    brandVariant === 'ed' ||
    brandVariant === 'mi' ||
    brandVariant === 'su' ||
    brandVariant === 'fr';

  const isFamilyFirst = isFamilyFirstCA || isFamilyFirstUS;

  const enablePopConsTrimFilters = _.get(
    display,
    'enablePopConsTrimFilters',
    false
  );

  const isLNVEnabled = (isFamilyFirstCA) => {
    if (isFamilyFirstCA) {
      return false;
    }
    const lnv = _.get(appResources, 'callToActions').find(
      (cta) => cta.key === 'lnv'
    );
    if (lnv) {
      return lnv.enabled;
    }
    return true;
  };

  /*TODO: below, using default angle for trim card angle.  Eventually, we may need to have a unique piece of data for that.
   *  Default is meant for build view angles. Adding a new angle requires 3.0 backend support.  */

  const buildViewDefaultAngles = () => {
    return {
      exterior: _.get(appResources, 'exteriorAngles').find(
        (angle) => angle.defaultAngle === true
      ).value,
      interior: _.get(appResources, 'interiorAngles').find(
        (angle) => angle.defaultAngle === true
      ).value,
    };
  };

  const trimCardDefaultAngle = () => {
    return (
      _.get(appResources, 'exteriorAngles').find(
        (angle) => angle.defaultAngle === true
      ).value || 'deg01'
    );
  };

  const cgiData = {
    cgiUrlTemplate: _.get(urls, 'cgiUrl'),
    cgiSizes: _.get(appResources, 'cgiSize'),
    buildViewDefaultAngles: buildViewDefaultAngles(),
    trimCardDefaultAngle: trimCardDefaultAngle(),
  };

  return {
    legacyServicesPath,
    servicesPath,
    v3ServicesPath,
    modelPath,
    uiPath,
    contextPath,
    version,
    language,
    country,
    brand,
    brandBase,
    brandVariant,
    assetsBrand,
    carline,
    year,
    bodyStyle,
    region,
    locale: `${language}-${country}`,
    title,
    isAccessoriesOpenDefault,
    isLiteRenderMode,
    isDefaultRenderMode,
    isFleet: brandVariant === 'fleet',
    isFamilyFirstCA,
    isFamilyFirstUS,
    isFamilyFirst,
    isCvo: brandVariant === 'cvo',
    isMobile: deviceType === 'MOBILE',
    isQuantum: _.get(appResources, 'worldPlatformMode') === 'QUANTUM',
    isCanada: country === 'CA',
    isEvMode: evMode,
    baseUrl,
    homepageUrl,
    backOutLocationUrl,
    classicModel,
    featureGroup,
    deviceType,
    enableTrimFilterCollapseSingleDrive,
    enableConfigStep,
    enableModelsStep,
    enableTrimCompare,
    enablePaymentEstimator,
    enableChat,
    liveChatType,
    enableFallbackNavigation,
    enablePostalCode: isDefaultRenderMode,
    enableNotifications: isDefaultRenderMode,
    enableTutorial: isDefaultRenderMode,
    enableAuth,
    adChoicesUrl: _.get(urls, 'adChoicesUrl', ''),
    adChoicesCookiesUrl: _.get(urls, 'adChoicesCookiesUrl', ''),
    adChoicesId: _.get(appResources, 'adChoicesId', ''),
    authRedirectUrl: _.get(auth, 'redirectUrl', '/'),
    cgiData: cgiData,
    vehicleShopperParamsUrl,
    vehicleShopperCountUrl: _.get(urls, 'vehicleShopperCountUrl', ''),
    inventoryCountUrl: _.get(appResources, 'btl.serviceUrl', ''),
    defaultModelSelectorUrl: _.get(urls, 'backOutLocationUrl', '/'),
    vehicleShopperRadius: _.get(appResources, 'vehicleShopperRadius', 100),
    enableVehicleShopper: enableVehicleShopper,
    enableInventorySummaryCount: isLNVEnabled(isFamilyFirstCA),
    steps,
    assetsUrlTemplate,
    enableFeaturedBrandPromo,
    enableFeaturedSubscription,
    featuredContentUrl: _.get(urls, 'featuredContentUrl', ''),
    quantumData: _.get(appResources, 'quantum', {}),
    enablePopConsTrimFilters,
    hasTransactionId: hasTransactionId || !!sessionStorage[transactionIdKey],
    transactionId: transactionId || sessionStorage[transactionIdKey],
    transactionIdKey,
    lccKey,
  };
};

export const ModelInfoContext = React.createContext({});

export class ModelInfoProvider extends React.Component {
  constructor(props) {
    super(props);
    this.modelInfoContext = createModelInfo();
  }

  render() {
    return (
      <ModelInfoContext.Provider value={this.modelInfoContext}>
        {this.props.children}
      </ModelInfoContext.Provider>
    );
  }
}

export const useModelInfo = () => useContext(ModelInfoContext);

export const withModelInfo = (Component) => {
  const ModelInfoComponent = (props) => {
    return (
      <ModelInfoContext.Consumer>
        {(modelInfo) => <Component {...props} modelInfo={modelInfo} />}
      </ModelInfoContext.Consumer>
    );
  };
  const displayName = Component.displayName || Component.name || 'Component';
  ModelInfoComponent.displayName = `${displayName}WitModelInfo`;
  return ModelInfoComponent;
};

export default ModelInfoContext;
